import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { Button, Screen } from '@dia/ui';
import { supabase } from '../supabase';
import { HeaderActions, Notice, PasswordField } from './lib/ui';
import { saveOutcome } from './lib/password';
import { readRecoveryLink } from './lib/recoveryLink';

/**
 * Where the "Forgot your password?" email link lands. Supabase's own
 * recovery link puts a session in the URL before this screen ever mounts, so
 * a signed-in-for-recovery visitor is exactly who this page expects; nobody
 * types a URL here on their own account's say-so.
 */
export function OwnerResetPassword() {
  const { t } = useT();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [expired, setExpired] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [problem, setProblem] = useState<'mismatch' | 'signal' | null>(null);
  const [rejected, setRejected] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // The client does not read the link's #access_token by itself (it is built
    // with detectSessionInUrl off), so this screen does: take the tokens from
    // the address, start the recovery session, then wipe them from the address.
    let alive = true;
    const link = readRecoveryLink(window.location.hash);
    const finish = (ok: boolean) => {
      if (!alive) return;
      if (ok) setReady(true);
      else setExpired(true);
    };
    if (link.kind === 'session') {
      window.history.replaceState(null, '', window.location.pathname);
      void supabase.auth
        .setSession({ access_token: link.accessToken, refresh_token: link.refreshToken })
        .then(({ data, error }) => finish(!error && Boolean(data.session)))
        .catch(() => finish(false));
    } else if (link.kind === 'expired') {
      finish(false);
    } else {
      // Opened again after the tokens were wiped: fine if the session is still here.
      void supabase.auth
        .getSession()
        .then(({ data }) => finish(Boolean(data.session)))
        .catch(() => finish(false));
    }
    return () => {
      alive = false;
    };
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setProblem('mismatch');
      return;
    }
    setBusy(true);
    setProblem(null);
    setRejected(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      const outcome = saveOutcome(error);
      if (outcome.kind === 'signal') {
        setProblem('signal');
        return;
      }
      if (outcome.kind === 'rejected') {
        setRejected(outcome.reason);
        return;
      }
      setDone(true);
    } catch {
      setProblem('signal');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Screen title={t('ops.owner.reset_title')} actions={<HeaderActions badge={false} />}>
        <div className="mt-4 flex flex-col gap-4">
          <Notice tone="ok">{t('ops.owner.reset_success')}</Notice>
          <Button block size="lg" onClick={() => navigate('/dueno')}>
            {t('ops.owner.submit')}
          </Button>
        </div>
      </Screen>
    );
  }

  if (expired) {
    return (
      <Screen title={t('ops.owner.reset_title')} actions={<HeaderActions badge={false} />}>
        <Notice tone="danger">{t('ops.owner.reset_expired')}</Notice>
      </Screen>
    );
  }

  if (!ready) return null;

  return (
    <Screen title={t('ops.owner.reset_title')} actions={<HeaderActions badge={false} />}>
      <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-5">
        <PasswordField
          label={t('ops.owner.reset_password')}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          revealed={revealed}
          onToggle={() => setRevealed((v) => !v)}
        />
        <PasswordField
          label={t('ops.owner.reset_confirm')}
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          revealed={revealed}
          onToggle={() => setRevealed((v) => !v)}
        />
        {problem && (
          <Notice tone="danger">
            {problem === 'mismatch' ? t('ops.owner.reset_mismatch') : t('ops.owner.no_signal')}
          </Notice>
        )}
        {rejected && <Notice tone="danger">{t('ops.owner.reset_rejected', { reason: rejected })}</Notice>}
        <Button type="submit" block size="lg" busy={busy} busyLabel={t('loading')}>
          {t('ops.owner.reset_submit')}
        </Button>
      </form>
    </Screen>
  );
}
